import { useEffect, useRef } from 'react';
import {
  SUBSCRIPTION_SUMMARY_REFRESH_EVENT,
  FOCUS_REFRESH_MIN_INTERVAL_MS,
  shouldRefreshSummaryOnFocus
} from '../utils/subscriptionSummaryRefresh';

/**
 * Wires the refresh triggers for the global credit-summary fetch (see utils/subscriptionSummaryRefresh.js):
 * once on mount, on `SUBSCRIPTION_SUMMARY_REFRESH_EVENT`, and on window focus / tab visible when the
 * cached summary is older than `minIntervalMs`. Deliberately has no dependency on the current route.
 *
 * @param {() => (Promise<unknown>|void)} fetchSummary - fetches and applies the summary; identity changes are ignored
 * @param {{ minIntervalMs?: number, now?: () => number }} [options]
 */
export default function useSubscriptionSummaryRefreshTriggers(fetchSummary, options = {}) {
  const { minIntervalMs = FOCUS_REFRESH_MIN_INTERVAL_MS, now = () => Date.now() } = options;
  const fetchRef = useRef(fetchSummary);
  const lastFetchedAtRef = useRef(null);

  useEffect(() => {
    fetchRef.current = fetchSummary;
  }, [fetchSummary]);

  useEffect(() => {
    let cancelled = false;
    let inFlight = null;
    let rerunRequested = false;

    // Serialise fetches. An explicit subscription-change event that arrives while a fetch is in flight
    // is coalesced into exactly one trailing fetch once the current one settles, so it is neither dropped
    // nor counted as "fetched" for the focus throttle. Focus / visibility signals never queue a trailing
    // fetch: returning to a tab emits both `visibilitychange` and `focus`, and the fetch the first one
    // started is exactly what the second one would ask for.
    const run = ({ queueIfBusy = true } = {}) => {
      if (inFlight) {
        if (queueIfBusy) rerunRequested = true;
        return inFlight;
      }
      inFlight = (async () => {
        try {
          do {
            rerunRequested = false;
            try {
              await fetchRef.current?.();
            } finally {
              if (!cancelled) lastFetchedAtRef.current = now();
            }
          } while (rerunRequested && !cancelled);
        } finally {
          inFlight = null;
        }
      })();
      return inFlight;
    };

    const onRefreshRequested = () => {
      run();
    };

    const onFocusOrVisible = () => {
      const isVisible = typeof document === 'undefined' || document.visibilityState === 'visible';
      if (
        shouldRefreshSummaryOnFocus({
          lastFetchedAt: lastFetchedAtRef.current,
          now: now(),
          isVisible,
          minIntervalMs
        })
      ) {
        run({ queueIfBusy: false });
      }
    };

    run();
    window.addEventListener(SUBSCRIPTION_SUMMARY_REFRESH_EVENT, onRefreshRequested);
    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    return () => {
      cancelled = true;
      window.removeEventListener(SUBSCRIPTION_SUMMARY_REFRESH_EVENT, onRefreshRequested);
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
    };
    // Mount-only: triggers must not re-run on route or callback identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
